import { useState, useEffect, useRef } from "react";
import { useLang, LangToggle } from "./i18n.jsx";
import { useHostBackButton } from "./miniapp/backbutton.js";
// Palette comes from the ONE source of truth — this file used to keep its own
// exact copy of all 15 colours.
import { BLACK, C, WHITE } from "./design-system.js";

const FONT = "'Estedad', sans-serif";

// Hero/accent PNGs (transparent bg). Upload to /media/onboarding/. Missing ones hide gracefully.
// Bump IMG_VER whenever an image is replaced, to bust the CDN cache.
// Self-hosted in the repo (frontend/public/onboarding) → shipped with the build,
// no external dependency, never lost.
const IMG_BASE = "/onboarding";
const IMG_VER = "6";
const imgUrl = (src) => `${IMG_BASE}/${src}?v=${IMG_VER}`;

// Glowing sparks scattered over the subject (stable positions, varied timing).
const SPARKS = Array.from({ length: 14 }).map(() => ({
  x: Math.random() * 90 + 3,
  y: Math.random() * 90 + 3,
  size: Math.random() * 3 + 2,        // 2–5px
  dur: Math.random() * 2 + 2.2,       // 2.2–4.2s
  delay: Math.random() * 3.5,
}));

const SLIDES = [
  {
    key: "fleeting",
    titleK: "ob.s1.title",
    descK: "ob.s1.desc",
    accent: C.gold,
    layers: [
      // Stars sit BEHIND the child as a glowing backdrop, not in a corner.
      { src: "p1-stars.svg", kind: "bg", size: 380, top: "0%", delay: 0.1 },
      { src: "p1-child.webp", kind: "hero", anim: "rise" },
      { src: "p1-moon.webp", kind: "float", anim: "drift", top: "6%", left: "10%", size: 96, delay: 0.3 },
    ],
  },
  {
    key: "growth",
    titleK: "ob.s2.title",
    descK: "ob.s2.desc",
    accent: C.teal,
    layers: [
      // p2-child is tightly cropped (subject fills the frame) → a narrower width so
      // it matches the other slides' heroes instead of towering over them.
      { src: "p2-child.webp", kind: "hero", anim: "rise", w: "min(56%, 205px)" },
      { src: "p2-sprout.webp", kind: "float", anim: "drift", bottom: "19%", left: "6%", size: 90, delay: 0.4 },
      { src: "p2-butterflies.webp", kind: "float", anim: "glowFloat", top: "11%", right: "8%", size: 86, delay: 0.6 },
    ],
  },
  {
    key: "personal",
    titleK: "ob.s3.title",
    descK: "ob.s3.desc",
    accent: C.purple,
    layers: [
      { src: "p3-hero.webp", kind: "hero", anim: "rise" },
      { src: "p3-book.webp", kind: "float", anim: "glowFloat", bottom: "20%", right: "10%", size: 88, delay: 0.4 },
      { src: "p3-key.webp", kind: "float", anim: "drift", top: "14%", left: "12%", size: 58, delay: 0.6 },
    ],
  },
  {
    key: "science",
    titleK: "ob.s4.title",
    quoteK: "ob.s4.quote",
    authorK: "ob.s4.author",
    descK: "ob.s4.desc",
    accent: C.teal,
    layers: [
      { src: "p4-child.webp", kind: "hero", anim: "rise" },
      { src: "p4-fingerprint.webp", kind: "float", anim: "drift", top: "8%", right: "10%", size: 64, delay: 0.3 },
      { src: "p4-book.webp", kind: "float", anim: "glowFloat", bottom: "18%", left: "8%", size: 138, delay: 0.5 },
    ],
  },
];

function Stars({ count = 40 }) {
  const stars = useRef(
    Array.from({ length: count }).map(() => ({
      w: Math.random() * 2 + 1, op: Math.random() * 0.4 + 0.1,
      top: Math.random() * 100, left: Math.random() * 100,
      dur: Math.random() * 3 + 2, delay: Math.random() * 3,
    }))
  ).current;
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
      {stars.map((s, i) => (
        <div key={i} style={{
          position: "absolute", width: s.w, height: s.w, borderRadius: "50%",
          background: C.cream, opacity: s.op, top: `${s.top}%`, left: `${s.left}%`,
          animation: `twinkle ${s.dur}s ease-in-out infinite`, animationDelay: `${s.delay}s`,
        }} />
      ))}
    </div>
  );
}

// Spark emitters at FIXED world angles around the ring. Each fires exactly when
// the rotating head passes it (staggered delay), so sparks appear to be shed BY
// the head, then detach — drifting outward with a per-spark pseudo-random jitter,
// shrinking and fading — while the head keeps spinning. They do NOT rotate with
// the head (they live outside the rotating layer).
const SPARK_EMITTERS = Array.from({ length: 16 }, (_, i) => {
  const rnd = (k) => { const x = Math.sin((i + 1) * k) * 43758.5453; return x - Math.floor(x); };
  return {
    ang: i * 22.5,                        // fixed world angle on the ring
    // each emitter throws a small BURST of 2 sparks with differing trajectories
    sparks: [
      { jx: (rnd(12.9898) - 0.5) * 0.3, fly: -(0.16 + rnd(78.233) * 0.24), s: 0.6 + rnd(37.719) * 0.7, dt: 0 },
      { jx: (rnd(51.7) - 0.5) * 0.3, fly: -(0.13 + rnd(91.3) * 0.22), s: 0.55 + rnd(7.77) * 0.65, dt: 0.06 },
    ],
  };
});

// Magical spinner — a gentle glowing head circles the ring, shedding sparks that
// detach, scatter outward, and burn out — like a real comet/sparkler.
export function MagicSpinner({ size = 40, color = C.gold }) {
  const ring = Math.max(2, Math.round(size * 0.1));
  const head = Math.max(3, Math.round(size * 0.16));
  const dur = 1.8;   // slower, gentler
  const maskR = `radial-gradient(farthest-side, transparent calc(100% - ${ring}px), ${BLACK} calc(100% - ${ring}px))`;
  return (
    <span style={{ position: "relative", display: "inline-block", width: size, height: size, verticalAlign: "middle" }}>
      <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: `conic-gradient(from 0deg, ${color} 0deg, ${color}00 200deg, ${color}00 360deg)`, WebkitMask: maskR, mask: maskR, animation: `magspin ${dur}s linear infinite` }} />
      <span style={{ position: "absolute", inset: 0, animation: `magspin ${dur}s linear infinite` }}>
        <span style={{ position: "absolute", top: -head * 0.08, left: "50%", width: head, height: head, marginLeft: -head / 2, borderRadius: "50%", background: WHITE, boxShadow: `0 0 ${Math.round(size * 0.3)}px ${color}, 0 0 ${Math.round(size * 0.13)}px ${WHITE}` }} />
      </span>
      {SPARK_EMITTERS.map((e, i) => {
        // magspin is counterclockwise; the head passes world angle `ang` at
        // t = dur·((360−ang)%360)/360 — that's this emitter's firing delay.
        const base = dur * (((360 - e.ang) % 360) / 360);
        return (
          <span key={i} style={{ position: "absolute", inset: 0, transform: `rotate(${e.ang}deg)`, pointerEvents: "none" }}>
            {e.sparks.map((sp, j) => {
              const d = Math.max(1.5, size * 0.09 * sp.s);
              return (
                <span key={j} style={{ position: "absolute", top: 0, left: "50%", width: d, height: d, marginLeft: -d / 2, borderRadius: "50%", background: WHITE, opacity: 0, boxShadow: `0 0 ${Math.round(size * 0.1)}px ${color}`, "--jx": `${(sp.jx * size).toFixed(1)}px`, "--fly": `${(sp.fly * size).toFixed(1)}px`, animation: `magfly ${dur}s linear ${(base + sp.dt).toFixed(2)}s infinite` }} />
              );
            })}
          </span>
        );
      })}
    </span>
  );
}

export default function Onboarding({ onFinish }) {
  const { t, dir, rtl } = useLang();
  const [page, setPage] = useState(0);
  const touchStartX = useRef(null);
  const isLast = page === SLIDES.length - 1;

  // Preload AND decode all slide art up front. Decoding (not just loading) means
  // every <img> paints at its real size the instant it mounts — no load-then-jump
  // reflow, so the entrance animation is the only motion the user sees.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const urls = SLIDES.flatMap(s => s.layers.map(l => imgUrl(l.src)));
    if (!urls.length) { setReady(true); return; }
    let alive = true;
    const one = (u) => {
      const im = new Image();
      im.src = u;
      return im.decode ? im.decode().catch(() => {}) : new Promise(res => { im.onload = im.onerror = res; });
    };
    Promise.all(urls.map(one)).then(() => { if (alive) setReady(true); });
    const safety = setTimeout(() => { if (alive) setReady(true); }, 6000);
    return () => { alive = false; clearTimeout(safety); };
  }, []);

  useEffect(() => {
    if (document.getElementById("zoha-onboarding-style")) return;
    const el = document.createElement("style");
    el.id = "zoha-onboarding-style";
    el.textContent = `
      /* Fixed to the visible viewport so the CTA never falls below the fold.
         dvh excludes mobile browser/system bars; vh is the universal fallback. */
      /* FULL-BLEED: fill the whole display and carry the safe-area insets as our OWN
         padding (the parent is .app-root--bleed, which drops its padding for us).
         Why it matters: padding lives INSIDE the background box, so the background,
         <Stars> and the glow (absolute, inset:0 → positioned against the padding box)
         all reach the very top, while the flex CONTENT still starts below the status
         bar. Reserving the inset on the PARENT instead left a dark bar above the
         artwork on Telegram fullscreen and in the Eitaa mini app.
         The content box stays exactly one visible viewport tall, so the CTA never
         falls below the fold. overflow:hidden guarantees the page never scrolls. */
      .ob-root {
        height: 100dvh; max-height: 100dvh; overflow: hidden;
        box-sizing: border-box;
        padding-top: var(--tg-safe-top, 0px);
        padding-bottom: var(--tg-safe-bottom, 0px);
      }
      /* Magical spinner: a glowing ring whose tail throws scattering sparks. */
      @keyframes magspin  { to { transform: rotate(-360deg); } }
      /* One spark life: born bright at the ring (as the head passes), detaches and
         drifts outward with sideways jitter, shrinks and burns out — then waits
         (invisible) for the head's next pass. */
      @keyframes magfly { 0%{opacity:0;transform:translate(0,0) scale(1)} 3%{opacity:1} 46%{opacity:0;transform:translate(var(--jx,0px),var(--fly,-12px)) scale(0.3)} 100%{opacity:0;transform:translate(var(--jx,0px),var(--fly,-12px)) scale(0.3)} }
      @keyframes twinkle { 0%,100%{opacity:0.1} 50%{opacity:0.5} }
      @keyframes obRise { 0%{opacity:0;transform:translateY(40px) scale(0.92)} 100%{opacity:1;transform:translateY(0) scale(1)} }
      @keyframes obDrift { 0%{opacity:0;transform:translateY(20px)} 100%{opacity:1;transform:translateY(0)} }
      @keyframes obFadeIn { 0%{opacity:0} 100%{opacity:1} }
      @keyframes obTextIn { 0%{opacity:0;transform:translateY(16px)} 100%{opacity:1;transform:translateY(0)} }

      /* Hero loops: gentle breathing + a slow light/colour shift (never static) */
      @keyframes obBreathe { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-6px) scale(1.025)} }
      @keyframes obHeroLight { 0%,100%{filter:brightness(1) saturate(1) drop-shadow(0 10px 26px ${C.bgDeep})} 50%{filter:brightness(1.1) saturate(1.12) drop-shadow(0 10px 30px ${C.bgDeep})} }

      /* Pulsing aura behind the child — looks like they're glowing */
      @keyframes obHalo { 0%,100%{opacity:0.55;transform:scale(0.92)} 50%{opacity:1;transform:scale(1.12)} }

      /* Background starfield: slow drift + glow so the stars feel alive */
      @keyframes obStarBg { 0%,100%{opacity:0.7;transform:translateX(-50%) scale(1) rotate(0deg)} 50%{opacity:1;transform:translateX(-50%) scale(1.05) rotate(1.5deg)} }
      @keyframes obStarGlow { 0%,100%{filter:drop-shadow(0 0 3px ${C.goldLight}66)} 50%{filter:drop-shadow(0 0 16px ${C.goldLight}cc)} }

      /* Glowing sparks drifting over the subject */
      @keyframes obSpark {
        0%   { opacity:0; transform: translateY(6px) scale(0.4); }
        25%  { opacity:1; }
        60%  { opacity:0.8; }
        100% { opacity:0; transform: translateY(-14px) scale(1.1); }
      }

      /* Varied accent float loops so nothing repeats identically */
      @keyframes obBob { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
      @keyframes obSway { 0%,100%{transform:translateY(-2px) rotate(-5deg)} 50%{transform:translateY(-14px) rotate(5deg)} }
      @keyframes obOrbit { 0%,100%{transform:translate(0,0) rotate(-4deg)} 33%{transform:translate(6px,-12px) rotate(3deg)} 66%{transform:translate(-6px,-6px) rotate(-2deg)} }
      @keyframes obAccentGlow { 0%,100%{filter:drop-shadow(0 0 6px ${C.gold}44)} 50%{filter:drop-shadow(0 0 16px ${C.gold}aa)} }
    `;
    document.head.appendChild(el);
  }, []);

  function next() {
    if (isLast) { onFinish?.(); return; }
    setPage(p => Math.min(SLIDES.length - 1, p + 1));
  }
  function skip() { onFinish?.(); }

  // The host back arrow turns the carousel back a page (same as a back-swipe);
  // on the first slide there is nowhere to go, so it stays hidden.
  useHostBackButton(() => setPage(p => Math.max(0, p - 1)), { active: page > 0 });
  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    // Advance in reading order: RTL (Persian) turns pages left→right (dx>0);
    // LTR (English) turns right→left (dx<0). Back is the opposite.
    const advance = rtl ? dx > 50 : dx < -50;
    const back    = rtl ? dx < -50 : dx > 50;
    if (advance) next();
    else if (back) setPage(p => Math.max(0, p - 1));
    touchStartX.current = null;
  }

  const slide = SLIDES[page];

  // Hold on a magical spinner until the art is preloaded → snappy, no flicker.
  if (!ready) {
    return (
      <div className="ob-root" style={{ background: C.bgDeep, fontFamily: FONT, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        <Stars count={40} />
        <MagicSpinner size={56} />
        <div style={{ color: C.creamDim, fontSize: 14, marginTop: 22, position: "relative" }}>{t("ob.loading")}</div>
      </div>
    );
  }

  return (
    <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
      className="ob-root"
      style={{ background: C.bgDeep, fontFamily: FONT, direction: dir, display: "flex", flexDirection: "column", position: "relative", overflow: "hidden" }}>
      <Stars count={50} />
      <div style={{ position: "absolute", top: "18%", left: "50%", transform: "translateX(-50%)", width: 320, height: 320, borderRadius: "50%", background: `radial-gradient(circle, ${slide.accent}22 0%, transparent 70%)`, pointerEvents: "none", transition: "background 0.5s ease" }} />

      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 20px" }}>
        <LangToggle />
        {!isLast ? (
          <button onClick={skip} style={{ background: "none", border: "none", color: C.textMid, fontFamily: FONT, fontSize: 15, cursor: "pointer", padding: "6px 4px" }}>
            {t("ob.skip")}
          </button>
        ) : <span />}
      </div>

      {/* Artwork stage. overflow is VISIBLE on purpose: the aura is 420px and the
          starfield layer sits at the very top, so a clipping box cut both off —
          worst on hosts that report a big safe-area inset (Eitaa), where this box
          is shortest. The art is decorative, so let it bleed; `.ob-root` still has
          overflow:hidden, which clips at the DISPLAY edge and keeps the page from
          scrolling. The negative top margin lets the art reclaim the inset strip so
          it renders full-bleed, while the header/footer stay inset and readable. */}
      <div key={slide.key} style={{ position: "relative", flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 0, margin: "0 12px", marginTop: "calc(-1 * var(--tg-safe-top, 0px))", overflow: "visible" }}>
        {/* Pulsing aura behind the child — the "glowing kid" effect.
            Larger than the hero so it spills past their silhouette and reads as a glow. */}
        <div style={{
          position: "absolute", top: "50%", left: "50%", width: 420, height: 420,
          marginTop: -210, marginLeft: -210, borderRadius: "50%",
          background: `radial-gradient(circle, ${slide.accent}77 0%, ${slide.accent}3A 32%, ${slide.accent}14 52%, transparent 70%)`,
          zIndex: 1, pointerEvents: "none", mixBlendMode: "screen",
          willChange: "transform, opacity", backfaceVisibility: "hidden",
          animation: "obHalo 4.5s ease-in-out 0.5s infinite",
        }} />

        {slide.layers.map((layer, i) => {
          const isHero = layer.kind === "hero";
          const isBg   = layer.kind === "bg";

          if (isBg) {
            // Background starfield behind the hero — drifts + glows.
            return (
              <img key={i} src={imgUrl(layer.src)} alt=""
                onError={e => { e.currentTarget.style.display = "none"; }}
                style={{
                  position: "absolute", left: "50%", top: layer.top ?? "0%",
                  width: layer.size, height: "auto", objectFit: "contain", zIndex: 0, opacity: 0.85,
                  filter: `drop-shadow(0 0 10px ${C.goldLight}55)`,
                  willChange: "transform, opacity", backfaceVisibility: "hidden",
                  animation: `obFadeIn 1s ease ${layer.delay || 0}s both, obStarBg 7s ease-in-out ${(layer.delay || 0) + 0.5}s infinite`,
                }} />
            );
          }

          if (isHero) {
            return (
              <img key={i} src={imgUrl(layer.src)} alt=""
                onError={e => { e.currentTarget.style.display = "none"; }}
                style={{
                  // Default hero width; a tightly-cropped subject can override via `w`
                  // so it renders at the same visual size as the other slides' heroes.
                  position: "relative", width: layer.w || "min(74%, 290px)", height: "auto",
                  maxHeight: "100%", objectFit: "contain", zIndex: 2,   // scale down on short screens
                  // entrance once, then a forever-breathing transform. The depth glow is a
                  // STATIC drop-shadow (animating filter every frame caused the jank).
                  filter: "drop-shadow(0 10px 28px ${C.bgDeep})",
                  willChange: "transform", backfaceVisibility: "hidden", transform: "translateZ(0)",
                  animation: `obRise 0.8s cubic-bezier(0.34,1.4,0.5,1) both, obBreathe 6s ease-in-out 0.8s infinite`,
                }} />
            );
          }

          // Floating accent — varied loop per index so they don't move in sync.
          const floatLoops = ["obBob", "obSway", "obOrbit"];
          const loop = floatLoops[i % floatLoops.length];
          const dur  = 4 + (i % 3) * 1.1;  // 4s / 5.1s / 6.2s
          return (
            <img key={i} src={imgUrl(layer.src)} alt=""
              onError={e => { e.currentTarget.style.display = "none"; }}
              style={{
                position: "absolute",
                ...(layer.top != null ? { top: layer.top } : {}),
                ...(layer.bottom != null ? { bottom: layer.bottom } : {}),
                ...(layer.left != null ? { left: layer.left } : {}),
                ...(layer.right != null ? { right: layer.right } : {}),
                width: layer.size, height: "auto", objectFit: "contain", zIndex: 3,
                filter: `drop-shadow(0 0 10px ${C.gold}55)`,
                willChange: "transform", backfaceVisibility: "hidden", transform: "translateZ(0)",
                animation: `obDrift 0.8s ease ${layer.delay || 0}s both, ${loop} ${dur}s ease-in-out ${(layer.delay || 0) + 0.8}s infinite`,
              }} />
          );
        })}

        {/* Glowing sparks over the subject — contained to the hero's central scope */}
        <div style={{ position: "absolute", top: "50%", left: "50%", width: 230, height: 320, marginTop: -170, marginLeft: -115, zIndex: 4, pointerEvents: "none" }}>
          {SPARKS.map((s, i) => {
            const col = [slide.accent, C.goldLight, C.cream][i % 3];
            return (
              <span key={`${slide.key}-${i}`} style={{
                position: "absolute", left: `${s.x}%`, top: `${s.y}%`,
                width: s.size, height: s.size, borderRadius: "50%",
                background: col, boxShadow: `0 0 ${s.size * 2.5}px ${col}`,
                animation: `obSpark ${s.dur}s ease-in-out ${s.delay}s infinite`,
              }} />
            );
          })}
        </div>
      </div>

      <div key={`txt-${slide.key}`} style={{ position: "relative", padding: "0 28px 12px", textAlign: "center" }}>
        <h2 style={{ color: C.cream, fontSize: slide.quoteK ? 20 : 22, fontWeight: 800, lineHeight: 1.6, margin: "0 0 12px", animation: "obTextIn 0.6s ease 0.2s both" }}>{t(slide.titleK)}</h2>
        {slide.quoteK && (
          <div style={{ margin: "0 0 12px", animation: "obTextIn 0.6s ease 0.3s both" }}>
            <p style={{ color: C.cream, fontSize: 16, lineHeight: 1.95, margin: 0, fontWeight: 600 }}>«{t(slide.quoteK)}»</p>
            <div style={{ color: slide.accent, fontSize: 13.5, fontWeight: 700, marginTop: 9 }}>{t(slide.authorK)}</div>
          </div>
        )}
        <p style={{ color: C.creamDim, fontSize: slide.quoteK ? 14.5 : 16, lineHeight: slide.quoteK ? 1.85 : 2, margin: 0, animation: `obTextIn 0.6s ease ${slide.quoteK ? "0.42s" : "0.35s"} both` }}>{t(slide.descK)}</p>
      </div>

      <div style={{ position: "relative", display: "flex", justifyContent: "center", gap: 8, padding: "12px 0 6px", flexShrink: 0 }}>
        {SLIDES.map((_, i) => (
          <div key={i} style={{ width: i === page ? 26 : 8, height: 8, borderRadius: 8, background: i === page ? slide.accent : C.border, transition: "all 0.3s ease" }} />
        ))}
      </div>

      <div style={{ position: "relative", padding: "8px 20px calc(20px + env(safe-area-inset-bottom))", flexShrink: 0 }}>
        <button onClick={next}
          style={{ width: "100%", border: "none", borderRadius: 16, padding: "16px 0", fontFamily: FONT, fontSize: 17, fontWeight: 800, cursor: "pointer", color: C.bgDeep, background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, boxShadow: `0 4px 24px ${C.gold}44`, transition: "transform 0.12s ease" }}
          onPointerDown={e => (e.currentTarget.style.transform = "scale(0.96)")}
          onPointerUp={e => (e.currentTarget.style.transform = "scale(1)")}
          onPointerLeave={e => (e.currentTarget.style.transform = "scale(1)")}>
          {isLast ? t("ob.start") : t("ob.continue")}
        </button>
      </div>
    </div>
  );
}
